import React, { memo, useCallback, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { Box, IconButton, InputAdornment, Typography } from '@mui/material';
import Flags from 'app/shared/componentsRestyled/common/Flags';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldInputType, numberFormatProps } from 'constants/fields';
import { DEFAULT_DECIMAL_SCALE } from 'constants/number';
import { PendingChangeKind } from 'constants/pendingChanges';
import { normalizePhoneInput, onPhoneFieldInput } from 'utils/phone';

import { HideIcon, ShowIcon } from '../../icons';
import TextFieldCDS from '../TextFieldCDS';

import {
  ADORNMENT_TYPE_TO_POSITION_MAP,
  AdornmentPosition,
  AdornmentType,
} from './DataFieldCDS.constants';
import {
  endAdornmentStyles,
  flagsStyles,
  getAdornmentSignStyles,
  inputStyles,
  maskIconStyles,
  startAdornmentStyles,
} from './DataFieldCDS.styles';
import {
  checkValue,
  doTypeDependingAction,
  getInputTypeTags,
  getMinValue,
  NUMBER_FORMAT_INPUT_EVENT_SOURCE_TYPE,
} from './DataFieldCDS.utils';

const DataFieldCDS = ({
  type = FieldInputType.TEXT,
  onChange = _.noop,
  name = '',
  maxValue,
  minValue,
  dataTestId,
  decimalScale = DEFAULT_DECIMAL_SCALE,
  isNumericString = false,
  disabled = false,
  error = false,
  adornmentType = AdornmentType.START,
  size,
  isHidden = false,
  ...props
}) => {
  const {
    isMaskFormat,
    isTelExtensionFormat,
    isCurrencyInputType,
    isPercentageInputType,
    isPhoneType,
  } = getInputTypeTags({ type });

  const [isMasked, setIsMasked] = useState(isMaskFormat);

  const toggleMask = useCallback(() => setIsMasked(prev => !prev), []);

  const isSmall = size === 'small';

  const onChangeValue = useCallback(
    ({ value, formattedValue }, { source }) => {
      if (source === NUMBER_FORMAT_INPUT_EVENT_SOURCE_TYPE.PROP) {
        return;
      }

      const correctValue = value
        ? doTypeDependingAction({
            value,
            formattedValue,
            type,
            maxValue,
            minValue,
            action: getMinValue,
          })
        : value;

      onChange({
        target: {
          name,
          value: correctValue,
          dataset: { novalidate: props.noValidate },
        },
      });
    },
    [name, onChange, type, maxValue, minValue, props.noValidate],
  );

  const onAllowed = useCallback(
    ({ value, formattedValue }) =>
      !value ||
      doTypeDependingAction({
        value,
        formattedValue,
        type,
        maxValue,
        minValue,
        action: checkValue,
      }),
    [type, maxValue, minValue],
  );

  const inputProps = useMemo(
    () => ({
      ...(isCurrencyInputType && {
        [adornmentType]: (
          <InputAdornment
            className="input-adornment"
            component={Box}
            position={ADORNMENT_TYPE_TO_POSITION_MAP[adornmentType]}
            sx={adornmentType === AdornmentType.START ? startAdornmentStyles : endAdornmentStyles}
          >
            <Typography sx={getAdornmentSignStyles({ isSmall })} variant="bold14">
              $
            </Typography>
          </InputAdornment>
        ),
        sx: inputStyles,
      }),
      ...(isMaskFormat && {
        endAdornment: (
          <InputAdornment
            className="input-adornment"
            component={IconButton}
            disabled={disabled}
            position={AdornmentPosition.END}
            size="small"
            sx={maskIconStyles}
            onClick={toggleMask}
          >
            {isMasked ? <ShowIcon size={20} /> : <HideIcon size={20} />}
          </InputAdornment>
        ),
      }),
      ...(isPercentageInputType && {
        [adornmentType]: (
          <InputAdornment
            className="input-adornment"
            component={Box}
            position={ADORNMENT_TYPE_TO_POSITION_MAP[adornmentType]}
            sx={adornmentType === AdornmentType.START ? startAdornmentStyles : endAdornmentStyles}
          >
            <Typography sx={getAdornmentSignStyles({ isSmall })} variant="bold14">
              %
            </Typography>
          </InputAdornment>
        ),
        sx: inputStyles,
      }),
      ...(isTelExtensionFormat && {
        startAdornment: (
          <InputAdornment
            className="input-adornment"
            component={Box}
            position={AdornmentPosition.START}
            sx={startAdornmentStyles}
          >
            <Flags disabled code="US" customStyles={flagsStyles} width={14} />
          </InputAdornment>
        ),
        sx: inputStyles,
      }),
    }),
    [
      isCurrencyInputType,
      adornmentType,
      isSmall,
      disabled,
      isMasked,
      isMaskFormat,
      toggleMask,
      isPercentageInputType,
      isTelExtensionFormat,
    ],
  );

  const onInputValue = useMemo(() => {
    if (!isPhoneType) {
      return null;
    }

    return onPhoneFieldInput;
  }, [isPhoneType]);

  const onPasteValue = useMemo(() => {
    if (!isPhoneType) {
      return null;
    }

    return e => {
      const result = normalizePhoneInput(e.clipboardData?.getData('text'));

      if (result === null) {
        return;
      }

      e.preventDefault();
      onChange({
        target: {
          name,
          value: result,
          dataset: { novalidate: props.noValidate },
        },
      });
    };
  }, [isPhoneType, name, onChange, props.noValidate]);

  return (
    <NumberFormat
      {...numberFormatProps[type]}
      decimalScale={decimalScale}
      fixedDecimalScale={isCurrencyInputType}
      {...props}
      InputProps={inputProps}
      customInput={TextFieldCDS}
      data-testid={dataTestId}
      disabled={disabled}
      error={error}
      formOnChange={onChange}
      isAllowed={onAllowed}
      isHidden={isHidden}
      isMaskFormat={isMaskFormat}
      isMasked={isMasked}
      isNumericString={isNumericString}
      name={name}
      size={size}
      onInput={onInputValue}
      onPaste={onPasteValue}
      onValueChange={onChangeValue}
    />
  );
};

DataFieldCDS.propTypes = {
  name: PropTypes.string,
  type: PropTypes.string,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxValue: PropTypes.number,
  minValue: PropTypes.number,
  maxLength: PropTypes.number,
  decimalScale: PropTypes.number,
  noValidate: PropTypes.bool,
  adornmentType: PropTypes.string,
  isNumericString: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
  fieldChangeKind: PropTypes.oneOf(Object.values(PendingChangeKind)),
  isHidden: PropTypes.bool,
  error: PropTypes.bool,
  size: PropTypes.string,
  onChange: PropTypes.func,
};

export default memo(DataFieldCDS);
